import { ThemedText } from "@/components/themed-text";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { isLocationOnlyService } from "@/lib/validation/booking";
import Ionicons from "@react-native-vector-icons/ionicons";
import React from "react";
import { StyleProp, StyleSheet, View, ViewStyle } from "react-native";

interface FloatingAddressCardProps {
  fromAddress: string;
  toAddress?: string;
  topInset: number;
  serviceType: string;
  style?: StyleProp<ViewStyle>;
}

export const FloatingAddressCard: React.FC<FloatingAddressCardProps> = ({
  fromAddress,
  toAddress,
  topInset,
  serviceType,
  style,
}) => {
  const { theme } = useAuth();
  const locationOnly = isLocationOnlyService(serviceType);

  return (
    <View
      style={[
        styles.floatingAddresses,
        { top: topInset + 10, left: 70, backgroundColor: theme.background },
        style,
      ]}
    >
      <View style={styles.routeSection}>
        <View style={styles.routeIconColumn}>
          <View style={styles.pickupIconOuter}>
            <View style={styles.pickupIconInner} />
          </View>
          {toAddress ? (
            <View style={styles.routeDashedLine}>
              {Array.from({ length: 3 }).map((_, index) => (
                <View key={index} style={styles.routeDash} />
              ))}
            </View>
          ) : null}
          {toAddress ? (
            <Ionicons name="location" size={16} color="#E11D48" />
          ) : null}
        </View>

        <View style={styles.routeDetails}>
          <View style={styles.routeStop}>
            <ThemedText style={styles.addressLabel} type="extraSmall">
              {locationOnly ? "Vehicle Location" : "Pickup point"}
            </ThemedText>
            <ThemedText
              style={styles.addressText}
              type="small"
              numberOfLines={1}
            >
              {fromAddress}
            </ThemedText>
          </View>

          {toAddress ? (
            <View style={styles.routeStop}>
              <ThemedText style={styles.addressLabel} type="extraSmall">
                Dropoff
              </ThemedText>
              <ThemedText
                style={styles.addressText}
                type="small"
                numberOfLines={1}
              >
                {toAddress}
              </ThemedText>
            </View>
          ) : null}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  floatingAddresses: {
    position: "absolute",
    right: 20,
    borderRadius: 16,
    padding: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
    zIndex: 20,
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.1)",
  },
  routeSection: {
    flexDirection: "row",
    gap: 10,
  },
  routeIconColumn: {
    alignItems: "center",
    paddingTop: 2,
  },
  pickupIconOuter: {
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: BrandColors.primary,
    alignItems: "center",
    justifyContent: "center",
  },
  pickupIconInner: {
    width: 7,
    height: 7,
    borderRadius: 3.5,
    backgroundColor: BrandColors.white,
  },
  routeDashedLine: {
    alignItems: "center",
    gap: 2,
    marginVertical: 4,
  },
  routeDash: {
    width: 2,
    height: 4,
    borderRadius: 1,
    backgroundColor: BrandColors.mediumGray,
  },
  routeDetails: {
    flex: 1,
    gap: 12,
  },
  routeStop: {
    gap: 2,
  },
  addressLabel: {
    fontSize: 11,
    fontWeight: "700",
    color: BrandColors.mediumGray,
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  addressText: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "500",
  },
});
